import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { DoorOpen, Download, FileText } from 'lucide-react';
import { Panel, Field, FormRow, fmtDate } from './design';
import { useConfirm } from '../../components/ConfirmDialog';

// Leaving, step by step. What happened decides the steps: an exit, a change of provider, a death,
// the provider ceasing services (14 days' written notice, services continue meanwhile), or a move
// between two of the provider's own NAPS services. Visits after the last day are cancelled and the
// workers told; the rest is a checklist with the dates Services Australia sets.

const WHAT = [
  ['exit', 'no_longer_needed', 'Left our services'],
  ['exit', 'residential', 'Moved into residential care'],
  ['transfer', 'provider_change', 'Changed to another provider'],
  ['death', 'deceased', 'Passed away'],
  ['ceasing', 'provider_ceasing', 'We are ceasing their services'],
  ['naps_transfer', 'naps_transfer', 'Moving to another of our NAPS services'],
];
const INP = 'input-field';
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
const plus = (ymd, n) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const KIND_WORD = { exit: 'Left', transfer: 'Changed provider', death: 'Passed away', ceasing: 'We cease services', naps_transfer: 'Moved NAPS service' };

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
  } catch (e) {
    let msg = 'Could not download it';
    try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
    toast.error(msg);
  }
}

export default function ExitWorkflow({ plan, onSaved }) {
  const [exits, setExits] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // { id, fields } while an exit's details are open
  const confirm = useConfirm();
  const load = useCallback(() => {
    if (!plan.client_id) return;
    api.get('/aged-care/exits', { params: { client_id: plan.client_id } }).then((r) => setExits((r.data || []).filter((e) => !e.funding_id || e.funding_id === plan.id))).catch(() => setExits([]));
  }, [plan.client_id, plan.id]);
  useEffect(() => { load(); }, [load]);
  if (!plan.id) return null;

  const pick = WHAT.find(([, reason]) => reason === form?.reason) || WHAT[0];
  const kind = pick[0];
  const start = async () => {
    const lastDay = form.exitDate ? fmtDate(form.exitDate) : 'the last day';
    const ok = await confirm(kind === 'naps_transfer'
      ? 'Their claims move to the new NAPS service from that date.'
      : `Visits after ${lastDay} are cancelled and the rostered workers told. This cannot be undone from here.`,
    { title: 'Record this?', confirmLabel: 'Record it', danger: kind !== 'naps_transfer' });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post('/aged-care/exits', { fundingId: plan.id, kind, reason: form.reason, exitDate: form.exitDate, noticeDate: form.noticeDate,
        grounds: form.grounds, continuityPlan: form.continuityPlan, gainingProvider: form.gainingProvider, newNapsServiceId: form.newNaps,
        estateName: form.estateName, estateEmail: form.estateEmail, estateAddress: form.estateAddress });
      toast.success(`${KIND_WORD[r.data.kind]}: recorded.${r.data.cancelled_visits ? ` ${r.data.cancelled_visits} later visit${r.data.cancelled_visits === 1 ? '' : 's'} cancelled and the workers told.` : ''}`);
      setForm(null); load(); if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); } finally { setBusy(false); }
  };
  const check = async (e, key, done) => {
    try { await api.post(`/aged-care/exits/${e.id}/check`, { key, done }); load(); if (onSaved && key === 'departure') onSaved(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not update it'); }
  };
  const save = async (e, body) => {
    try { await api.put(`/aged-care/exits/${e.id}`, body); toast.success('Saved'); setEditing(null); load(); } catch (err) { toast.error(err.response?.data?.message || 'Could not save'); }
  };
  const finalStatement = async (e) => {
    try { const r = await api.post(`/aged-care/exits/${e.id}/final-statement`); toast.success(`Final statement for ${r.data.period} made. Send it from Statements.`); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not make it'); }
  };
  const set = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target.value }));
  const exited = plan.exited_at ? String(plan.exited_at).slice(0, 10) : null;
  const hasOpen = exits.some((e) => e.status === 'open' && e.kind !== 'naps_transfer');

  return (
    <Panel title="Leaving" icon={DoorOpen} bodyClass="px-4 py-3 space-y-3"
      actions={!form && !hasOpen && <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({ reason: 'no_longer_needed', exitDate: today(), noticeDate: today() })}>Record what happened</button>}>
      {!exits.length && !form && (
        <p className="text-[13px] text-slate-500">{exited ? `Exited on ${fmtDate(exited)}.` : 'Still receiving services.'}</p>
      )}
      {form && (
        <div className="space-y-3">
          <Field label="What happened">{(id) => (
            <select id={id} className={INP} value={form.reason} onChange={set('reason')}>{WHAT.map(([, r, l]) => <option key={r} value={r}>{l}</option>)}</select>
          )}</Field>
          {kind === 'ceasing' ? (
            <>
              <FormRow cols={2}>
                <Field label="Notice given on">{(id) => <input id={id} type="date" className={INP} value={form.noticeDate} onChange={set('noticeDate')} />}</Field>
                <Field label="Last day of services" hint="At least 14 days after the notice. Services continue until then.">{(id) => <input id={id} type="date" className={INP} min={plus(form.noticeDate || today(), 14)} value={form.exitDate < plus(form.noticeDate || today(), 14) ? plus(form.noticeDate || today(), 14) : form.exitDate} onChange={set('exitDate')} />}</Field>
              </FormRow>
              <Field label="Grounds" required hint="Why services are ending. It goes in the written notice.">{(id) => <textarea id={id} rows={2} className={INP} value={form.grounds || ''} onChange={set('grounds')} />}</Field>
              <Field label="Continuity of care" hint="Who takes over, and how the move is supported.">{(id) => <textarea id={id} rows={2} className={INP} value={form.continuityPlan || ''} onChange={set('continuityPlan')} />}</Field>
            </>
          ) : (
            <FormRow cols={2}>
              <Field label={kind === 'death' ? 'Date of death' : kind === 'naps_transfer' ? 'Date of the move' : 'Exit date'}>{(id) => <input id={id} type="date" className={INP} value={form.exitDate} onChange={set('exitDate')} />}</Field>
              {kind === 'transfer' && <Field label="New provider" required>{(id) => <input id={id} className={INP} value={form.gainingProvider || ''} onChange={set('gainingProvider')} />}</Field>}
              {kind === 'naps_transfer' && <Field label="NAPS service ID they move to" required>{(id) => <input id={id} className={INP} value={form.newNaps || ''} onChange={set('newNaps')} />}</Field>}
            </FormRow>
          )}
          {kind === 'death' && (
            <FormRow cols={3}>
              <Field label="Estate contact">{(id) => <input id={id} className={INP} value={form.estateName || ''} onChange={set('estateName')} />}</Field>
              <Field label="Their email">{(id) => <input id={id} type="email" className={INP} value={form.estateEmail || ''} onChange={set('estateEmail')} />}</Field>
              <Field label="Postal address">{(id) => <input id={id} className={INP} value={form.estateAddress || ''} onChange={set('estateAddress')} />}</Field>
            </FormRow>
          )}
          <p className="text-[12px] text-slate-500">{kind === 'naps_transfer' ? 'They stay with you; their claims move to the new NAPS service.' : 'Visits after the last day are cancelled without charge, and the rostered workers are told.'}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={start}>{busy ? 'Saving' : 'Record'}</button>
          </div>
        </div>
      )}
      {exits.map((e) => (
        <div key={e.id} className="rounded-lg border border-slate-200 p-3 text-[13px] space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium text-slate-900">{KIND_WORD[e.kind] || e.kind}: {fmtDate(e.exit_date)}{e.gaining_provider ? ` to ${e.gaining_provider}` : ''}{e.new_naps_service_id ? ` (NAPS ${e.new_naps_service_id})` : ''}</span>
            <span className={`badge ${e.status === 'complete' ? 'badge-green' : 'badge-yellow'}`}>{e.status === 'complete' ? 'All steps done' : `${e.steps.filter((s) => !s.done).length} steps left`}</span>
          </div>
          {e.cancelled_visits > 0 && <p className="text-slate-500">{e.cancelled_visits} later visit{e.cancelled_visits === 1 ? '' : 's'} cancelled.</p>}
          <div className="flex flex-wrap gap-2">
            {e.steps.some((s) => s.key === 'final_statement') && <button type="button" className="btn-secondary btn-sm" onClick={() => finalStatement(e)}><FileText size={13} /> Make the final statement</button>}
            {e.kind === 'transfer' && <button type="button" className="btn-secondary btn-sm" onClick={() => download(`/aged-care/exits/${e.id}/handover`, 'handover.pdf')}><Download size={13} /> Handover pack</button>}
            {e.kind === 'ceasing' && <button type="button" className="btn-secondary btn-sm" onClick={() => download(`/aged-care/exits/${e.id}/notice-letter`, 'notice.pdf')}><Download size={13} /> Notice letter</button>}
          </div>
          <ul className="space-y-1">
            {e.steps.map((s) => {
              const late = !s.done && s.due && s.due < today();
              return (
                <li key={s.key} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={s.done} onChange={(ev) => check(e, s.key, ev.target.checked)} aria-label={s.title} />
                  <span className={s.done ? 'text-slate-500 line-through' : 'text-slate-800'}>
                    {s.title}
                    <span className={`ml-1 text-[12px] ${late ? 'text-red-700' : 'text-slate-500'}`}>{s.done ? `done ${fmtDate(s.doneAt)}${s.doneBy ? ` by ${s.doneBy}` : ''}` : `by ${fmtDate(s.due)}`}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          {e.kind === 'death' && <p className="text-[12px] text-slate-600">Estate contact: {e.estate_name || 'not recorded'}{e.estate_email ? `, ${e.estate_email}` : ''}{e.estate_address ? `, ${e.estate_address}` : ''}</p>}
          {e.kind === 'ceasing' && e.grounds && <p className="text-[12px] text-slate-600">Grounds: {e.grounds}</p>}
          {editing && editing.id === e.id ? (
            <div className="rounded-lg bg-slate-50 p-3 space-y-2">
              {e.kind === 'death' && (
                <FormRow cols={3}>
                  <Field label="Estate contact">{(id) => <input id={id} className={INP} value={editing.f.estateName} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, estateName: v } })); }} />}</Field>
                  <Field label="Their email">{(id) => <input id={id} type="email" className={INP} value={editing.f.estateEmail} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, estateEmail: v } })); }} />}</Field>
                  <Field label="Postal address">{(id) => <input id={id} className={INP} value={editing.f.estateAddress} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, estateAddress: v } })); }} />}</Field>
                </FormRow>
              )}
              {e.kind === 'transfer' && <Field label="New provider">{(id) => <input id={id} className={INP} value={editing.f.gainingProvider} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, gainingProvider: v } })); }} />}</Field>}
              {e.kind === 'ceasing' && <>
                <Field label="Grounds">{(id) => <textarea id={id} rows={2} className={INP} value={editing.f.grounds} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, grounds: v } })); }} />}</Field>
                <Field label="Continuity of care">{(id) => <textarea id={id} rows={2} className={INP} value={editing.f.continuityPlan} onChange={(ev) => { const v = ev.target.value; setEditing((x) => ({ ...x, f: { ...x.f, continuityPlan: v } })); }} />}</Field>
              </>}
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                <button type="button" className="btn-primary btn-sm" onClick={() => save(e, editing.f)}>Save</button>
              </div>
            </div>
          ) : ['death', 'transfer', 'ceasing'].includes(e.kind) && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => setEditing({ id: e.id, f: e.kind === 'death'
              ? { estateName: e.estate_name || '', estateEmail: e.estate_email || '', estateAddress: e.estate_address || '' }
              : e.kind === 'transfer' ? { gainingProvider: e.gaining_provider || '' } : { grounds: e.grounds || '', continuityPlan: e.continuity_plan || '' } })}>Edit details</button>
          )}
        </div>
      ))}
    </Panel>
  );
}
